import { FileText, FlaskConical, Headset, Mail, Monitor, Pencil, Plus, Unlink } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Section } from '@/components/common/Blocks'
import { EmptyState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { fmtDateTime } from '../../../shared/format'
import { downloadCrmFile, useEmails, useFiles, useRequests, useTrials, useWork } from '../../api'
import { FILE_CATEGORIES, type FileCategory } from '../../types'
import type { CrmDialog } from '../CrmDialogs'
import { toastError, useCrm } from '../../ui'
import { RequestList, WorkList } from '../RecordLists'
import { TrialList } from '../TrialList'
import { FootprintSection } from './FootprintSection'
import { useCrmDeletes } from '../useCrmDeletes'
import { useAuth } from '@/lib/auth/AuthProvider'
import { CRM_MAIL_SEND_PERM, MAIL_SEND_PERM } from '../../permissions'

type Props = { customerId: string; canWrite: boolean; open: (d: CrmDialog) => void }

export function DeliveryTab({ customerId, canWrite, open }: Props) {
  const { t } = useTranslation()
  const work = useWork(customerId)
  const trials = (useTrials().data ?? []).filter((x) => x.customerId === customerId)
  const deletes = useCrmDeletes()
  const rows = work.data ?? []
  return (
    <div className="space-y-4">
      <Section
        title={t('crm.trial.title')}
        icon={FlaskConical}
        bodyClassName="p-0"
        actions={
          canWrite ? (
            <Button size="sm" variant="outline" onClick={() => open({ kind: 'trial' })}>
              <Plus />
              {t('crm.trial.add')}
            </Button>
          ) : null
        }
      >
        <TrialList rows={trials} canWrite={canWrite} onExtend={(x) => open({ kind: 'extendTrial', trial: x })} onConvert={(x) => open({ kind: 'convertTrial', trial: x })} />
      </Section>
      <Section
        title={t('crm.work.title')}
        icon={Monitor}
        bodyClassName="p-0"
        actions={
          canWrite ? (
            <Button size="sm" onClick={() => open({ kind: 'work', work: null })}>
              <Plus />
              {t('crm.work.add')}
            </Button>
          ) : null
        }
      >
        <WorkList rows={rows} canWrite={canWrite} onEdit={(w) => open({ kind: 'work', work: w })} onDelete={(w) => void deletes.work(w)} />
      </Section>
      <FootprintSection customerId={customerId} />
    </div>
  )
}

export function RequestsTab({ customerId, canWrite, open }: Props) {
  const { t } = useTranslation()
  const requests = useRequests(customerId)
  const deletes = useCrmDeletes()
  const rows = requests.data ?? []
  return (
    <Section
      title={t('crm.request.title')}
      icon={Headset}
      bodyClassName="p-0"
      actions={
        canWrite ? (
          <Button size="sm" onClick={() => open({ kind: 'request', request: null })}>
            <Plus />
            {t('crm.request.add')}
          </Button>
        ) : null
      }
    >
      <RequestList rows={rows} canWrite={canWrite} onEdit={(r) => open({ kind: 'request', request: r })} onDelete={(r) => void deletes.request(r)} />
    </Section>
  )
}

export function EmailsTab({ customerId, canWrite, open }: Props) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const canSend = canWrite && can(CRM_MAIL_SEND_PERM) && can(MAIL_SEND_PERM)
  const emails = useEmails(customerId)
  const { staffName } = useCrm()
  const [openId, setOpenId] = useState<string | null>(null)
  const rows = emails.data ?? []
  return (
    <Section
      title={t('crm.email.listTitle')}
      icon={Mail}
      bodyClassName="p-0"
      actions={
        canSend ? (
          <Button size="sm" onClick={() => open({ kind: 'email' })}>
            <Mail />
            {t('crm.email.title')}
          </Button>
        ) : null
      }
    >
      {rows.length === 0 ? (
        <EmptyState icon={Mail} title={t('crm.email.empty')} />
      ) : (
        <ul className="divide-y">
          {rows.map((e) => (
            <li key={e.id}>
              <button type="button" onClick={() => setOpenId(openId === e.id ? null : e.id)} aria-expanded={openId === e.id} className="w-full px-4 py-3 text-left transition-colors hover:bg-muted/40">
                <p className="font-medium">{e.subject}</p>
                <p className="text-xs text-muted-foreground">
                  {fmtDateTime(e.sentAt)} · {staffName(e.by) !== '—' ? staffName(e.by) : e.by} → {e.to}
                </p>
                {openId === e.id ? <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{e.body}</p> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

export function FilesTab({ customerId, canWrite, open }: Props) {
  const { t } = useTranslation()
  const files = useFiles(customerId)
  const { staffName } = useCrm()
  const [cat, setCat] = useState<FileCategory | 'all' | 'unlinked'>('all')
  const all = files.data ?? []
  const rows = all.filter((f) => (cat === 'unlinked' ? !f.linked : f.linked && (cat === 'all' || f.category === cat)))
  return (
    <Section
      title={t('crm.files.title')}
      icon={FileText}
      bodyClassName="p-0"
      actions={
        canWrite ? (
          <Button size="sm" onClick={() => open({ kind: 'file', file: null })}>
            <Plus />
            {t('crm.files.upload')}
          </Button>
        ) : null
      }
    >
      <div className="border-b p-3">
        <Tabs value={cat} onValueChange={(v) => setCat(v as typeof cat)}>
          <TabsList size="sm">
            <TabsTrigger value="all">{t('common.all')}</TabsTrigger>
            {FILE_CATEGORIES.map((c) => (
              <TabsTrigger key={c} value={c}>
                {t(`crm.fileCategory.${c}`)}
              </TabsTrigger>
            ))}
            <TabsTrigger value="unlinked">{t('crm.files.unlinked')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {rows.length === 0 ? (
        <EmptyState icon={FileText} title={t('crm.files.empty')} />
      ) : (
        <ul className="divide-y">
          {rows.map((f) => (
            <li key={f.id} className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3', !f.linked && 'opacity-70')}>
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                <FileText className="size-5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1 basis-56">
                <p className="flex flex-wrap items-center gap-2 font-medium">
                  {f.title}
                  <Badge tone="info" className="text-[11px]">{t(`crm.fileCategory.${f.category}`)}</Badge>
                  <Badge tone="outline" className="text-[11px]">{t(`crm.visibility.${f.visibility}`)}</Badge>
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  <button type="button" className="hover:text-foreground hover:underline" onClick={() => void downloadCrmFile('file', f.id, f.fileName).catch(toastError)}>
                    {f.fileName}
                  </button>{' '}
                  · {fmtDateTime(f.uploadedAt)} · {staffName(f.uploadedBy)}
                </p>
                {!f.linked ? <p className="text-xs text-destructive">{t('crm.files.unlinkedReason', { reason: f.unlinkReason })}</p> : f.description ? <p className="line-clamp-1 text-xs text-muted-foreground">{f.description}</p> : null}
              </div>
              {canWrite && f.linked ? (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => open({ kind: 'file', file: f })}>
                    <Pencil />
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => open({ kind: 'unlink', file: f })}>
                    <Unlink />
                    {t('crm.unlink.short')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}
